import { useEffect, useRef, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

export default function ImageLightbox({ images, startIndex = 0, onClose, alt = "", actionLabel, onAction }) {
    const [index, setIndex] = useState(startIndex);
    const [zoom, setZoom] = useState(1);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const dragRef = useRef(null);
    const touchRef = useRef(null);
    // Separate ref to persist the "moved" flag after dragRef is cleared in endDrag
    const dragMovedRef = useRef(false);

    const reset = useCallback(() => {
        setZoom(1);
        setPos({ x: 0, y: 0 });
    }, []);

    const next = useCallback(() => {
        setIndex((i) => (i + 1) % images.length);
        reset();
    }, [images.length, reset]);

    const prev = useCallback(() => {
        setIndex((i) => (i - 1 + images.length) % images.length);
        reset();
    }, [images.length, reset]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            else if (e.key === "ArrowRight") next();
            else if (e.key === "ArrowLeft") prev();
            else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(5, z + 0.5));
            else if (e.key === "-" || e.key === "_") {
                setZoom((z) => {
                    const n = Math.max(1, z - 0.5);
                    if (n === 1) setPos({ x: 0, y: 0 });
                    return n;
                });
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [next, prev, onClose]);

    useEffect(() => {
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prevOverflow; };
    }, []);

    const onWheel = (e) => {
        if (e.deltaY === 0) return;
        e.preventDefault();
        const delta = e.deltaY < 0 ? 0.25 : -0.25;
        setZoom((z) => {
            const n = Math.max(1, Math.min(5, z + delta));
            if (n === 1) setPos({ x: 0, y: 0 });
            return n;
        });
    };

    // ── Mouse drag ──
    const onMouseDown = (e) => {
        if (zoom === 1) return;
        e.preventDefault();
        dragMovedRef.current = false;
        dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: pos.x, baseY: pos.y };
    };

    const onMouseMove = (e) => {
        const d = dragRef.current;
        if (!d) return;
        const dx = e.clientX - d.startX;
        const dy = e.clientY - d.startY;
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) dragMovedRef.current = true;
        setPos({ x: d.baseX + dx, y: d.baseY + dy });
    };

    const endDrag = () => { dragRef.current = null; };

    const onImageClick = (e) => {
        e.stopPropagation();
        // Use the separate ref — dragRef is already null here (cleared by onMouseUp → endDrag)
        if (dragMovedRef.current) {
            dragMovedRef.current = false;
            return;
        }
        if (zoom === 1) setZoom(2);
        else reset();
    };

    // ── Touch ──
    const onTouchStart = (e) => {
        if (e.touches.length === 1) {
            touchRef.current = {
                startX: e.touches[0].clientX,
                startY: e.touches[0].clientY,
                baseX: pos.x,
                baseY: pos.y,
                moved: false,
                pinching: false,
            };
        } else if (e.touches.length === 2) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            touchRef.current = {
                ...touchRef.current,
                pinching: true,
                startDist: Math.sqrt(dx * dx + dy * dy),
                startZoom: zoom,
            };
        }
    };

    const onTouchMove = (e) => {
        const t = touchRef.current;
        if (!t) return;

        if (e.touches.length === 2 && t.pinching) {
            e.preventDefault();
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const newZoom = Math.max(1, Math.min(5, t.startZoom * (dist / t.startDist)));
            if (newZoom === 1) setPos({ x: 0, y: 0 });
            setZoom(newZoom);
            return;
        }

        if (e.touches.length === 1) {
            const dx = e.touches[0].clientX - t.startX;
            const dy = e.touches[0].clientY - t.startY;

            if (zoom > 1) {
                e.preventDefault();
                t.moved = true;
                setPos({ x: t.baseX + dx, y: t.baseY + dy });
            } else {
                if (Math.abs(dx) > 5 || Math.abs(dy) > 5) t.moved = true;
            }
        }
    };

    const onTouchEnd = (e) => {
        const t = touchRef.current;
        if (!t) return;

        if (t.pinching) {
            // After pinch-zoom ends, reset pinching state but keep position tracking
            touchRef.current = null;
            return;
        }

        if (!t.moved && zoom === 1) {
            setZoom(2);
        } else if (!t.moved && zoom > 1) {
            // Only reset on tap — a drag that moved < threshold won't have t.moved=true,
            // so guard with a distance check to avoid false resets on very short pans
            reset();
        } else if (zoom === 1 && t.moved) {
            const dx = e.changedTouches[0].clientX - t.startX;
            if (Math.abs(dx) > 50) {
                if (dx < 0) next();
                else prev();
            }
        }
        // zoom > 1 && t.moved → finished a pan; do nothing (don't reset)

        touchRef.current = null;
    };

    return (
        <div
            className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm select-none"
            onClick={onClose}
            onWheel={onWheel}
            onMouseMove={onMouseMove}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            data-testid="lightbox"
        >
            {/* Header */}
            <div
                className="absolute top-0 left-0 right-0 flex items-center justify-between p-5 md:p-6 z-10 text-melilux-cream/85"
                onClick={(e) => e.stopPropagation()}
            >
                <span className="text-[0.7rem] uppercase tracking-[0.28em]" data-testid="lightbox-counter">
                    <span className="text-melilux-cream">{index + 1}</span> / {images.length}
                </span>
                <div className="flex items-center gap-2">
                    {actionLabel && onAction && (
                        <button
                            onClick={() => onAction(index)}
                            data-testid="lightbox-action"
                            className="mr-2 px-4 py-2 bg-melilux-gold text-melilux-bg text-[0.62rem] uppercase tracking-[0.25em] font-medium hover:bg-melilux-goldBright transition-colors"
                        >
                            {actionLabel}
                        </button>
                    )}
                    <button
                        onClick={() => setZoom((z) => { const n = Math.max(1, z - 0.5); if (n === 1) setPos({ x: 0, y: 0 }); return n; })}
                        data-testid="lightbox-zoom-out"
                        className="p-2 border border-white/15 hover:border-melilux-gold hover:text-melilux-gold transition-colors"
                        aria-label="Zoom out"
                    >
                        <ZoomOut size={15} strokeWidth={1.5} />
                    </button>
                    <span className="text-[0.62rem] uppercase tracking-[0.25em] text-melilux-cream/55 w-12 text-center" data-testid="lightbox-zoom-level">
                        {Math.round(zoom * 100)}%
                    </span>
                    <button
                        onClick={() => setZoom((z) => Math.min(5, z + 0.5))}
                        data-testid="lightbox-zoom-in"
                        className="p-2 border border-white/15 hover:border-melilux-gold hover:text-melilux-gold transition-colors"
                        aria-label="Zoom in"
                    >
                        <ZoomIn size={15} strokeWidth={1.5} />
                    </button>
                    <button
                        onClick={onClose}
                        data-testid="lightbox-close"
                        className="ml-3 p-2 border border-white/15 hover:border-melilux-gold hover:text-melilux-gold transition-colors"
                        aria-label="Close"
                    >
                        <X size={15} strokeWidth={1.5} />
                    </button>
                </div>
            </div>

            {/* Image */}
            <div className="w-full h-full flex items-center justify-center overflow-hidden">
                <img
                    src={images[index]}
                    alt={alt}
                    draggable={false}
                    onMouseDown={onMouseDown}
                    onClick={onImageClick}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    style={{
                        transform: `translate(${pos.x}px, ${pos.y}px) scale(${zoom})`,
                        transformOrigin: "center",
                        cursor: zoom > 1 ? (dragRef.current ? "grabbing" : "grab") : "zoom-in",
                        transition: dragRef.current || touchRef.current ? "none" : "transform 220ms cubic-bezier(0.16, 1, 0.3, 1)",
                        maxWidth: "92vw",
                        maxHeight: "82vh",
                        willChange: "transform",
                        touchAction: zoom > 1 ? "none" : "pan-y",
                    }}
                    className="object-contain"
                    data-testid="lightbox-image"
                />
            </div>

            {/* Nav */}
            {images.length > 1 && (
                <>
                    <button
                        onClick={(e) => { e.stopPropagation(); prev(); }}
                        data-testid="lightbox-prev"
                        className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 p-3 border border-white/15 hover:border-melilux-gold hover:text-melilux-gold text-melilux-cream/85 transition-colors bg-melilux-bg/40 backdrop-blur"
                        aria-label="Previous"
                    >
                        <ChevronLeft size={20} strokeWidth={1.5} />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); next(); }}
                        data-testid="lightbox-next"
                        className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 p-3 border border-white/15 hover:border-melilux-gold hover:text-melilux-gold text-melilux-cream/85 transition-colors bg-melilux-bg/40 backdrop-blur"
                        aria-label="Next"
                    >
                        <ChevronRight size={20} strokeWidth={1.5} />
                    </button>
                </>
            )}

            {/* Thumbnails */}
            {images.length > 1 && (
                <div
                    className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 max-w-[80vw] overflow-x-auto"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="lightbox-thumbs"
                >
                    {images.map((src, i) => (
                        <button
                            key={i}
                            onClick={() => { setIndex(i); reset(); }}
                            data-testid={`lightbox-thumb-${i}`}
                            className={`w-14 h-14 shrink-0 overflow-hidden ${
                                i === index ? "ring-1 ring-melilux-gold" : "opacity-60 hover:opacity-100"
                            } transition-opacity`}
                        >
                            <img src={src} alt="" className="w-full h-full object-cover" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}